// sapi-admin/src/pages/AdminHome.jsx - VERSION OPTIMISÉE AVEC CORRECTIONS ESLINT
import { Link, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useCallback  } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PiRound from '../assets/piRound.svg';
import { toast } from "react-toastify";
import { 
  collection,
  query, 
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { db } from '../firebase/config';
import {
  Home, Package, Users, ShoppingBag, Tag, Image as ImageIcon,
  Bell, LogOut, TrendingUp, ShoppingCart, Menu, X,
  ChevronRight, Activity, Zap, BarChart2,
  Truck, RefreshCw
} from "react-feather";
import { CreditCard, DollarSign, PiSquare, Store, MessageCircle, SettingsIcon } from "lucide-react";
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import AdminNotificationBell from "../components/AdminNotificationBell";
import AdminAvatarMenu from "../components/AdminAvatarMenu";
import FormatPrice from "../components/FormatPrice";
import { AdvancedFilters } from '../components/DashboardFilters';
import { signOut } from "firebase/auth";
import { auth } from "../firebase/config";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import NetworkStatus from '../components/NetworkStatus';
import adminLogo from '../assets/logos/logo-admin.png';
import { App } from '@capacitor/app';

// ─── Couleurs constantes ──────────────────────────────────────────────────
const PIE_COLORS = ["#F59E0B", "#3B82F6", "#8B5CF6", "#10B981", "#EF4444", "#6B7280"];
const STATUS_COLORS = { 
  Placed: "#F59E0B", 
  Approved: "#3B82F6", 
  Shipped: "#8B5CF6", 
  Delivered: "#10B981", 
  Declined: "#EF4444", 
  Cancelled: "#6B7280" 
};

// ─── Pi Icon ───────────────────────────────────────────────────────────────
const PiIcon = ({ size = 24, className = "" }) => (
  <img src={PiRound} alt="π" className={className} style={{ width: size, height: size }} />
);

// ─── Animated number counter avec effet "ticker" ─────────────────────────
const AnimatedNumber = ({ value, isPrice = false, previousValue = null }) => {
  const [display, setDisplay] = useState(0);
  const [isIncreasing, setIsIncreasing] = useState(false);
  
  useEffect(() => {
    let start = 0;
    const end = parseFloat(String(value).replace(/[^0-9.]/g, "")) || 0;
    if (end === 0) {
      setDisplay(0);
      return;
    }
    const duration = 800;
    const step = end / (duration / 16);
    let animationId = null;
    
    const animate = () => {
      start += step;
      if (start >= end) {
        setDisplay(end);
        return;
      }
      setDisplay(start);
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [value]);

  useEffect(() => {
    if (previousValue !== null && value > previousValue) {
      setIsIncreasing(true);
      const timer = setTimeout(() => setIsIncreasing(false), 500);
      return () => clearTimeout(timer);
    }
  }, [value, previousValue]);

  if (isPrice) {
    return (
      <span className={`transition-all duration-300 ${isIncreasing ? 'text-green-400 scale-110' : ''}`}>
        {FormatPrice(display)}
      </span>
    );
  }
  return (
    <span className={`transition-all duration-300 ${isIncreasing ? 'text-green-400 scale-110' : ''}`}>
      {Math.floor(display).toLocaleString()}
    </span>
  );
};

// ─── Stat Card avec effet de mise à jour ──────────────────────────────────
const StatCard = ({ title, value, icon: Icon, accent, trend, loading, index = 0, isPrice = false, previousValue = null }) => {
  const accents = {
    amber:  { ring: "ring-amber-500/30",  glow: "shadow-amber-500/20", text: "text-amber-400",  bg: "bg-amber-500/10",  dot: "bg-amber-400" },
    teal:   { ring: "ring-teal-500/30",   glow: "shadow-teal-500/20",  text: "text-teal-400",   bg: "bg-teal-500/10",   dot: "bg-teal-400" },
    violet: { ring: "ring-violet-500/30", glow: "shadow-violet-500/20", text: "text-violet-400", bg: "bg-violet-500/10", dot: "bg-violet-400" },
    rose:   { ring: "ring-rose-500/30",   glow: "shadow-rose-500/20",  text: "text-rose-400",   bg: "bg-rose-500/10",   dot: "bg-rose-400" },
    sky:    { ring: "ring-sky-500/30",    glow: "shadow-sky-500/20",   text: "text-sky-400",    bg: "bg-sky-500/10",    dot: "bg-sky-400" },
    orange: { ring: "ring-orange-500/30", glow: "shadow-orange-500/20", text: "text-orange-400", bg: "bg-orange-500/10", dot: "bg-orange-400" },
    pink:   { ring: "ring-pink-500/30",   glow: "shadow-pink-500/20",  text: "text-pink-400",   bg: "bg-pink-500/10",   dot: "bg-pink-400" },
    emerald:{ ring: "ring-emerald-500/30",glow: "shadow-emerald-500/20",text: "text-emerald-400",bg: "bg-emerald-500/10",dot: "bg-emerald-400" },
    green:  { ring: "ring-green-500/30",  glow: "shadow-green-500/20", text: "text-green-400",  bg: "bg-green-500/10",  dot: "bg-green-400" },
    cyan:   { ring: "ring-cyan-500/30",   glow: "shadow-cyan-500/20",  text: "text-cyan-400",   bg: "bg-cyan-500/10",   dot: "bg-cyan-400" },
    purple: { ring: "ring-purple-500/30", glow: "shadow-purple-500/20", text: "text-purple-400", bg: "bg-purple-500/10", dot: "bg-purple-400" },
    yellow: { ring: "ring-yellow-500/30", glow: "shadow-yellow-500/20",  text: "text-yellow-400",   bg: "bg-yellow-500/10",   dot: "bg-yellow-400" },
  };
  const c = accents[accent] || accents.amber;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      className={`relative overflow-hidden rounded-2xl ring-1 ${c.ring} bg-[#111827]/80 backdrop-blur-xl p-6 shadow-xl ${c.glow}`}
    >
      <div className={`absolute -top-8 -right-8 w-32 h-32 rounded-full ${c.bg} blur-2xl`} />
      <div className="relative flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-widest text-slate-400 font-medium mb-3">{title}</p>
          {loading ? (
            <div className="h-8 w-28 bg-slate-700/60 animate-pulse rounded-lg" />
          ) : (
            <p className="text-3xl font-bold text-white tracking-tight leading-none">
              <AnimatedNumber value={value} isPrice={isPrice} previousValue={previousValue} />
            </p>
          )}
          {trend && !loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 + index * 0.08 }}
              className={`flex items-center gap-1 mt-3 text-xs font-medium ${c.text}`}
            >
              <TrendingUp size={12} />
              <span>{trend}</span>
            </motion.div>
          )}
        </div>
        <div className={`flex-shrink-0 w-11 h-11 rounded-xl ${c.bg} flex items-center justify-center ring-1 ${c.ring}`}>
          {Icon === PiIcon ? <PiIcon size={22} /> : <Icon size={20} className={c.text} strokeWidth={1.8} />}
        </div>
      </div>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 0.3 + index * 0.08, duration: 0.6 }}
        className={`absolute bottom-0 left-0 h-0.5 w-full origin-left ${c.dot} opacity-60`}
      />
    </motion.div>
  );
};

// ─── Glass Card wrapper ────────────────────────────────────────────────────
const GlassCard = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`rounded-2xl bg-[#111827]/80 backdrop-blur-xl ring-1 ring-white/[0.06] shadow-xl overflow-hidden ${className}`}
  >
    {children}
  </motion.div>
);

const CardHeader = ({ title, subtitle, icon, action }) => (
  <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-white/[0.06]">
    <div className="flex items-center gap-3">
      {icon && <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">{icon}</div>}
      <div>
        <p className="text-sm font-semibold text-white">{title}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// AdminDashboard avec rafraîchissement automatique
// ═══════════════════════════════════════════════════════════════════════════
const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [productPerformance, setProductPerformance] = useState([]);
  const [statusDistribution, setStatusDistribution] = useState([]);

  const prevOrdersRef = useRef({ totalOrders: 0, totalRevenue: 0, averageOrderValue: 0 });
  const prevUsersRef = useRef({ totalUsers: 0 });

  const [dashboardData, setDashboardData] = useState({
    orders: { totalOrders: 0, totalRevenue: 0, monthlyRevenue: 0, averageOrderValue: 0, recentOrders: [] },
    eneo: { totalTransactions: 0, totalVolume: 0, totalKWh: 0, uniqueUsers: 0, recentRecharges: 0 },
    piSell: { totalRequests: 0, pendingManual: 0, completed: 0, totalPaid: 0, totalPiSold: 0, recentRequests: 0 },
    stores: { totalStores: 0, active: 0, pendingVerification: 0, totalRevenue: 0, totalProducts: 0, recentStores: 0 },
    users: { totalUsers: 0, newUsersThisMonth: 0 }
  });

  const unsubscribeRefs = useRef([]);

  // ─── Fonction pour forcer le rafraîchissement ─────────────────────────
  const refreshData = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // ─── Écouter le retour au premier plan (Capacitor App) ──────────────
  useEffect(() => {
  const listener = App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      console.log('📱 App active – refresh dashboard');
      refreshData();
    }
  });
  return () => {
    listener.then(l => l.remove());
  };
}, [refreshData]);

  // ─── Écouter l'événement personnalisé "refresh-dashboard" ────────────
  useEffect(() => {
    const handler = () => {
      console.log('🔄 Refresh triggered by custom event');
      refreshData();
    };
    window.addEventListener('refresh-dashboard', handler);
    return () => {
      window.removeEventListener('refresh-dashboard', handler);
    };
  }, [refreshData]);

  // ─── Listener des commandes ────────────────────────────────────────────
  useEffect(() => {
    const ordersQuery = query(
      collection(db, "orders"),
      orderBy("orderDate", "desc"),
      limit(200)
    );

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      let totalRevenue = 0;
      const recentOrders = [];
      const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      let monthlyRevenue = 0;

      snapshot.docs.forEach(doc => {
        const d = doc.data();
        const amount = d.totalAmount || d.total || d.amount || 0;
        totalRevenue += amount;

        if (d.orderDate && new Date(d.orderDate) >= firstDayOfMonth) {
          monthlyRevenue += amount;
        }
      });

      snapshot.docs.slice(0, 5).forEach(doc => {
        const d = doc.data();
        recentOrders.push({
          id: doc.id,
          orderId: d.orderId || doc.id.slice(0, 8).toUpperCase(),
          userName: d.userName || d.customerName || 'Unknown',
          userEmail: d.userEmail,
          total: d.totalAmount || d.total || d.amount || 0,
          status: d.status || 'Placed'
        });
      });

      prevOrdersRef.current = {
        totalOrders: snapshot.size,
        totalRevenue: totalRevenue,
        averageOrderValue: snapshot.size > 0 ? totalRevenue / snapshot.size : 0
      };

      setDashboardData(prev => ({
        ...prev,
        orders: {
          totalOrders: snapshot.size,
          totalRevenue,
          monthlyRevenue,
          averageOrderValue: snapshot.size > 0 ? totalRevenue / snapshot.size : 0,
          recentOrders
        }
      }));

      setLoading(false);
      setLastUpdate(new Date());
    }, (error) => {
      console.error('❌ Orders listener error:', error);
      setLoading(false);
    });

    unsubscribeRefs.current.push(unsubscribe);
    return () => unsubscribe();
  }, [refreshTrigger]); // ✅ dépend de refreshTrigger

  // ─── Listener ENEO ──────────────────────────────────────────────────────
  useEffect(() => {
    const eneoQuery = query(
      collection(db, "tokenTransactions"),
      orderBy("createdAt", "desc"),
      limit(200)
    );

    const unsubscribe = onSnapshot(eneoQuery, (snapshot) => {
      const transactions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const completed = transactions.filter(tx => tx.status === 'completed');
      const totalVolume = completed.reduce((sum, tx) => sum + (tx.amountPi || 0), 0);
      const totalKWh = completed.reduce((sum, tx) => sum + (tx.kWh || 0), 0);
      const uniqueUsers = new Set(transactions.map(tx => tx.userId).filter(Boolean)).size;

      const last7Days = new Date();
      last7Days.setDate(last7Days.getDate() - 7);
      const recentRecharges = completed.filter(tx => {
        const txDate = tx.createdAt?.toDate ? tx.createdAt.toDate() : new Date(tx.createdAt);
        return txDate >= last7Days;
      }).length;

      setDashboardData(prev => ({
        ...prev,
        eneo: {
          totalTransactions: snapshot.size,
          totalVolume,
          totalKWh,
          uniqueUsers,
          recentRecharges
        }
      }));
      setLastUpdate(new Date());
    }, (error) => {
      console.error('❌ ENEO listener error:', error);
    });

    unsubscribeRefs.current.push(unsubscribe);
    return () => unsubscribe();
  }, [refreshTrigger]);

  // ─── Listener Pi Sell ──────────────────────────────────────────────────
  useEffect(() => {
    const piSellQuery = query(
      collection(db, "piSellRequests"),
      orderBy("createdAt", "desc"),
      limit(200)
    );

    const unsubscribe = onSnapshot(piSellQuery, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const pendingManual = requests.filter(r => r.paymentStatus === 'pending_manual').length;
      const completed = requests.filter(r => r.paymentStatus === 'completed').length;
      const totalPaid = requests
        .filter(r => r.paymentStatus === 'completed')
        .reduce((sum, r) => sum + (r.fcfaAmount || 0), 0);
      const totalPiSold = requests
        .filter(r => r.paymentStatus === 'completed')
        .reduce((sum, r) => sum + (r.piAmount || 0), 0);

      const last7Days = new Date();
      last7Days.setDate(last7Days.getDate() - 7);
      const recentRequests = requests.filter(r => {
        const rDate = r.createdAt?.toDate ? r.createdAt.toDate() : new Date(r.createdAt);
        return rDate >= last7Days;
      }).length;

      setDashboardData(prev => ({
        ...prev,
        piSell: {
          totalRequests: snapshot.size,
          pendingManual,
          completed,
          totalPaid,
          totalPiSold,
          recentRequests
        }
      }));
      setLastUpdate(new Date());
    }, (error) => {
      console.error('❌ Pi Sell listener error:', error);
    });

    unsubscribeRefs.current.push(unsubscribe);
    return () => unsubscribe();
  }, [refreshTrigger]);

  // ─── Listener Pi Stores ────────────────────────────────────────────────
  useEffect(() => {
    const storesQuery = query(
      collection(db, "piStores"),
      orderBy("createdAt", "desc"),
      limit(200)
    );

    const unsubscribe = onSnapshot(storesQuery, (snapshot) => {
      const stores = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const active = stores.filter(s => s.isActive === true).length;
      const pendingVerification = stores.filter(s => s.paymentStatus === 'completed' && !s.isVerified).length;
      const totalRevenue = stores
        .filter(s => s.paymentStatus === 'completed')
        .reduce((sum, s) => sum + (s.paymentAmount || s.amount || 0.5), 0);
      const totalProducts = stores.reduce((sum, s) => sum + (s.products?.length || 0), 0);

      const last7Days = new Date();
      last7Days.setDate(last7Days.getDate() - 7);
      const recentStores = stores.filter(s => {
        const sDate = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
        return sDate >= last7Days;
      }).length;

      setDashboardData(prev => ({
        ...prev,
        stores: {
          totalStores: snapshot.size,
          active,
          pendingVerification,
          totalRevenue,
          totalProducts,
          recentStores
        }
      }));
      setLastUpdate(new Date());
    }, (error) => {
      console.error('❌ Stores listener error:', error);
    });

    unsubscribeRefs.current.push(unsubscribe);
    return () => unsubscribe();
  }, [refreshTrigger]);

  // ─── Listener Users ────────────────────────────────────────────────────
  useEffect(() => {
    const usersQuery = query(
      collection(db, "users"),
      limit(1000)
    );

    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      let newUsersThisMonth = 0;

      snapshot.docs.forEach(doc => {
        const d = doc.data();
        if (d.createdAt) {
          const createdAt = d.createdAt?.toDate ? d.createdAt.toDate() : new Date(d.createdAt);
          if (createdAt >= firstDayOfMonth) {
            newUsersThisMonth++;
          }
        }
      });

      prevUsersRef.current = {
        totalUsers: snapshot.size
      };

      setDashboardData(prev => ({
        ...prev,
        users: {
          totalUsers: snapshot.size,
          newUsersThisMonth
        }
      }));
      setLastUpdate(new Date());
    }, (error) => {
      console.error('❌ Users listener error:', error);
    });

    unsubscribeRefs.current.push(unsubscribe);
    return () => unsubscribe();
  }, [refreshTrigger]);

  // ─── Listener Monthly Revenue ──────────────────────────────────────────
  useEffect(() => {
    const ordersQuery = query(
      collection(db, "orders"),
      orderBy("orderDate", "desc"),
      limit(500)
    );

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      const now = new Date();
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(now.getMonth() - 6);
      const monthlyData = {};

      for (let i = 5; i >= 0; i--) {
        const m = new Date();
        m.setDate(1);
        m.setMonth(now.getMonth() - i);
        const k = `${m.getFullYear()}-${m.getMonth() + 1}`;
        monthlyData[k] = {
          month: m.toLocaleString('default', { month: 'short' }),
          year: m.getFullYear(),
          monthIndex: m.getMonth(),
          revenue: 0,
          orders: 0
        };
      }

      snapshot.docs.forEach(doc => {
        const d = doc.data();
        const amount = d.totalAmount || d.total || d.amount || 0;
        const od = new Date(d.orderDate);
        if (od >= sixMonthsAgo) {
          const k = `${od.getFullYear()}-${od.getMonth() + 1}`;
          if (monthlyData[k]) {
            monthlyData[k].revenue += amount;
            monthlyData[k].orders += 1;
          }
        }
      });

      const sorted = Object.values(monthlyData).sort((a, b) =>
        a.year !== b.year ? a.year - b.year : a.monthIndex - b.monthIndex
      );
      setMonthlyRevenue(sorted);
    });

    unsubscribeRefs.current.push(unsubscribe);
    return () => unsubscribe();
  }, [refreshTrigger]);

  // ─── Listener Status Distribution ──────────────────────────────────────
  useEffect(() => {
    const statusQuery = query(
      collection(db, "orders"),
      limit(500)
    );

    const unsubscribe = onSnapshot(statusQuery, (snapshot) => {
      const sc = {};
      snapshot.docs.forEach(doc => {
        const s = doc.data().status || "Unknown";
        if (!sc[s]) sc[s] = { status: s, count: 0 };
        sc[s].count += 1;
      });
      setStatusDistribution(Object.values(sc));
    });

    unsubscribeRefs.current.push(unsubscribe);
    return () => unsubscribe();
  }, [refreshTrigger]);

  // ─── Listener Top Products ─────────────────────────────────────────────
  useEffect(() => {
    const ordersQuery = query(
      collection(db, "orders"),
      limit(500)
    );

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      const productSales = {};

      snapshot.docs.forEach(doc => {
        const order = doc.data();
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach(item => {
            const productId = item.productId || item.id || 'unknown';
            const productName = item.name || item.productName || `Product ${productId.substring(0, 6)}`;
            const quantity = item.quantity || 1;

            if (!productSales[productId]) {
              productSales[productId] = {
                id: productId,
                name: productName,
                totalQuantity: 0,
                totalRevenue: 0,
                orders: 0
              };
            }
            productSales[productId].totalQuantity += quantity;
            productSales[productId].totalRevenue += (item.price || 0) * quantity;
            productSales[productId].orders += 1;
          });
        }
      });

      const sorted = Object.values(productSales)
        .sort((a, b) => b.totalQuantity - a.totalQuantity)
        .slice(0, 5);

      setProductPerformance(sorted);
      setLastUpdate(new Date());
    }, (error) => {
      console.error('❌ Top products listener error:', error);
    });

    unsubscribeRefs.current.push(unsubscribe);
    return () => unsubscribe();
  }, [refreshTrigger]);

  // ─── Nettoyage global ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      unsubscribeRefs.current.forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
      unsubscribeRefs.current = [];
    };
  }, []);

  // ─── Rendu ──────────────────────────────────────────────────────────────
  const now = new Date();
  const greeting = now.getHours() < 12 ? "Good morning" : now.getHours() < 18 ? "Good afternoon" : "Good evening";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 pb-8">
      
      <div className="flex justify-end items-center gap-2 text-[10px] text-slate-500">
        <RefreshCw size={10} className="animate-spin-slow" />
        <span>Live updates • {lastUpdate.toLocaleTimeString()}</span>
      </div>

      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0f172a] via-[#1a1f35] to-[#0f172a] ring-1 ring-white/[0.08] p-7 shadow-2xl"
      >
        <div className="absolute -top-16 -right-16 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 left-1/3 w-48 h-48 bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-400/80 font-medium mb-2">{greeting}</p>
            <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight">
              Welcome back, <span className="text-amber-400">Admin</span> 👋
            </h1>
            <p className="text-sm text-slate-400 mt-1.5">Live dashboard — updates in real time</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="px-4 py-2 rounded-xl bg-white/5 ring-1 ring-white/10 text-center">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Date</p>
              <p className="text-sm font-semibold text-white mt-0.5">
                {now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </p>
            </div>
            <div className="px-4 py-2 rounded-xl bg-amber-500/10 ring-1 ring-amber-500/30 text-center">
              <p className="text-[10px] uppercase tracking-widest text-amber-400/70">Network</p>
              <p className="text-sm font-bold text-amber-400 mt-0.5 flex items-center gap-1"><PiIcon size={14} /> Live</p>
            </div>
            <div className="px-3 py-2 rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/30">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                <span className="text-[9px] font-mono text-emerald-400">REAL-TIME</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="mb-6">
        <AdvancedFilters 
          onFilterChange={(filters) => {
            console.log('📊 Filtres appliqués:', filters);
          }} 
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Total Orders"      
          value={dashboardData.orders.totalOrders}           
          icon={ShoppingCart} 
          accent="amber"  
          loading={loading} 
          index={0}
          isPrice={false}
          previousValue={prevOrdersRef.current.totalOrders}
        />
        <StatCard 
          title="Total Revenue (π)" 
          value={dashboardData.orders.totalRevenue}          
          icon={PiIcon}       
          accent="violet" 
          loading={loading} 
          index={1} 
          trend={dashboardData.orders.monthlyRevenue > 0 ? `+${FormatPrice(dashboardData.orders.monthlyRevenue)} this month` : undefined}
          isPrice={true}
          previousValue={prevOrdersRef.current.totalRevenue}
        />
        <StatCard 
          title="Avg. Order Value"  
          value={dashboardData.orders.averageOrderValue}     
          icon={TrendingUp}   
          accent="teal"   
          loading={loading} 
          index={2}
          isPrice={true}
          previousValue={prevOrdersRef.current.averageOrderValue}
        />
        <StatCard 
          title="Total Users"       
          value={dashboardData.users.totalUsers}             
          icon={Users}        
          accent="rose"   
          loading={loading} 
          index={3} 
          trend={`+${dashboardData.users.newUsersThisMonth} new this month`}
          isPrice={false}
          previousValue={prevUsersRef.current.totalUsers}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        
        <GlassCard delay={0.1}>
          <div className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center relative">
                <Zap size={20} className="text-cyan-400" />
                {dashboardData.eneo.recentRecharges > 0 && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">ENEO Recharges</h3>
                <p className="text-[10px] text-slate-500">Prepaid meter transactions</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Total Recharges</p>
                <p className="text-xl font-bold text-cyan-400">{dashboardData.eneo.totalTransactions}</p>
              </div>
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Total Volume</p>
                <p className="text-sm font-semibold text-white">{FormatPrice(dashboardData.eneo.totalVolume)}</p>
              </div>
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Total Energy</p>
                <p className="text-sm font-semibold text-emerald-400">{dashboardData.eneo.totalKWh.toLocaleString()} kWh</p>
              </div>
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Unique Users</p>
                <p className="text-sm font-semibold text-white">{dashboardData.eneo.uniqueUsers}</p>
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-white/[0.06]">
              <span className="text-[10px] text-slate-500">Last 7 days</span>
              <div className="flex items-center gap-2">
                {dashboardData.eneo.recentRecharges > 0 && (
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                )}
                <span className="text-xs font-semibold text-cyan-400">+{dashboardData.eneo.recentRecharges} new</span>
              </div>
            </div>
          </div>
          <div className="px-5 pb-4">
            <Link to="/eneo-transactions" className="text-[10px] text-amber-400 hover:text-amber-300 transition flex items-center gap-1">
              View all <ChevronRight size={10} />
            </Link>
          </div>
        </GlassCard>

        <GlassCard delay={0.15}>
          <div className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center relative">
                <DollarSign size={20} className="text-orange-400" />
                {dashboardData.piSell.recentRequests > 0 && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Pi Sell Requests</h3>
                <p className="text-[10px] text-slate-500">Customer sell orders</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Total Requests</p>
                <p className="text-xl font-bold text-orange-400">{dashboardData.piSell.totalRequests}</p>
              </div>
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Pending</p>
                <p className="text-sm font-semibold text-yellow-400">{dashboardData.piSell.pendingManual}</p>
              </div>
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Completed</p>
                <p className="text-sm font-semibold text-emerald-400">{dashboardData.piSell.completed}</p>
              </div>
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Pi Sold</p>
                <p className="text-sm font-semibold text-amber-400">{FormatPrice(dashboardData.piSell.totalPiSold)}</p>
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-white/[0.06]">
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-slate-500">Last 7 days</span>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-semibold text-orange-400">+{dashboardData.piSell.recentRequests} new</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-slate-500">Paid:</span>
                <span className="text-sm font-semibold text-green-400">{dashboardData.piSell.totalPaid.toLocaleString()} FCFA</span>
              </div>
            </div>
          </div>
          <div className="px-5 pb-4">
            <Link to="/pi-sell-requests" className="text-[10px] text-amber-400 hover:text-amber-300 transition flex items-center gap-1">
              View all <ChevronRight size={10} />
            </Link>
          </div>
        </GlassCard>

        <GlassCard delay={0.2}>
          <div className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center relative">
                <Store size={20} className="text-violet-400" />
                {dashboardData.stores.recentStores > 0 && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Pi Stores</h3>
                <p className="text-[10px] text-slate-500">Registered merchants</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Total Stores</p>
                <p className="text-xl font-bold text-violet-400">{dashboardData.stores.totalStores}</p>
              </div>
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Active</p>
                <p className="text-sm font-semibold text-emerald-400">{dashboardData.stores.active}</p>
              </div>
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Pending Verify</p>
                <p className="text-sm font-semibold text-yellow-400">{dashboardData.stores.pendingVerification}</p>
              </div>
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <p className="text-[10px] text-slate-500">Total Products</p>
                <p className="text-sm font-semibold text-white">{dashboardData.stores.totalProducts}</p>
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-white/[0.06]">
              <span className="text-[10px] text-slate-500">Store Revenue (π)</span>
              <div className="flex items-center gap-2">
                {dashboardData.stores.recentStores > 0 && (
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                )}
                <span className="text-sm font-semibold text-amber-400">{FormatPrice(dashboardData.stores.totalRevenue)}</span>
              </div>
            </div>
          </div>
          <div className="px-5 pb-4">
            <Link to="/pi-stores" className="text-[10px] text-amber-400 hover:text-amber-300 transition flex items-center gap-1">
              View all <ChevronRight size={10} />
            </Link>
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        
        <GlassCard className="lg:col-span-3" delay={0.25}>
          <CardHeader
            title="Revenue Over Time"
            subtitle="Last 6 months"
            icon={<BarChart2 size={16} className="text-amber-400" />}
          />
          <div className="px-4 pt-2 pb-5 h-64">
            {monthlyRevenue.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyRevenue} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="month" stroke="#475569" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={v => FormatPrice(v)} stroke="#475569" tick={{ fontSize: 11 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#F59E0B" strokeWidth={2} fill="url(#gRev)" dot={{ r: 3, fill: "#F59E0B", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-center text-slate-500 text-sm">Loading revenue data...</p>
              </div>
            )}
          </div>
        </GlassCard>

        <GlassCard className="lg:col-span-2" delay={0.3}>
          <CardHeader
            title="Order Status"
            subtitle="Distribution"
            icon={<Activity size={16} className="text-violet-400" />}
          />
          <div className="px-4 pt-2 pb-5 h-64">
            {statusDistribution.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height="70%">
                  <PieChart>
                    <Pie data={statusDistribution} cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={3} dataKey="count" nameKey="status">
                      {statusDistribution.map((e, i) => (
                        <Cell key={i} fill={STATUS_COLORS[e.status] || PIE_COLORS[i % PIE_COLORS.length]} strokeWidth={0} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-1">
                  {statusDistribution.map((e, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-[10px] text-slate-400">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: STATUS_COLORS[e.status] || PIE_COLORS[i % PIE_COLORS.length] }} />
                      {e.status}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-center text-slate-500 text-sm">Loading status data...</p>
              </div>
            )}
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        <GlassCard delay={0.25}>
          <CardHeader
            title="Top Selling Products"
            subtitle="By quantity"
            icon={<TrendingUp size={16} className="text-teal-400" />}
          />
          <div className="px-4 pt-2 pb-5">
            {productPerformance.length === 0 ? (
              <div className="text-center text-slate-500 py-10 text-sm">
                No product data yet
              </div>
            ) : (
              <div className="space-y-2 pt-1">
                {productPerformance.map((p, i) => {
                  const max = productPerformance[0]?.totalQuantity || 1;
                  const pct = Math.round((p.totalQuantity / max) * 100);
                  return (
                    <motion.div
                      key={p.id || i}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + i * 0.06 }}
                      className="flex items-center gap-3 py-2"
                    >
                      <span className="w-5 text-xs font-bold text-slate-500 flex-shrink-0">#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium text-slate-200 truncate">{p.name}</p>
                          <p className="text-xs text-teal-400 font-semibold ml-2 flex-shrink-0">
                            {FormatPrice(p.totalRevenue)}
                          </p>
                        </div>
                        <div className="h-1.5 w-full bg-slate-700/60 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${pct}%` }}
                            transition={{ delay: 0.4 + i * 0.06, duration: 0.6, ease: "easeOut" }}
                            className="h-full rounded-full bg-gradient-to-r from-teal-500 to-teal-400"
                          />
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          {p.totalQuantity} units • {p.orders} order{p.orders !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </GlassCard>

        <GlassCard delay={0.4}>
          <CardHeader
            title="Recent Orders"
            subtitle="Latest transactions"
            icon={<ShoppingBag size={16} className="text-rose-400" />}
            action={
              <Link to="/orders" className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors font-medium">
                View all <ChevronRight size={14} />
              </Link>
            }
          />
          <div className="px-4 pt-1 pb-4">
            {dashboardData.orders.recentOrders.length === 0 ? (
              <div className="text-center text-slate-500 py-10 text-sm">No recent orders</div>
            ) : (
              <div className="divide-y divide-white/[0.04]">
                {dashboardData.orders.recentOrders.map((order, i) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-center justify-between py-3 group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-700/50 flex items-center justify-center flex-shrink-0">
                        <ShoppingBag size={13} className="text-slate-400" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-200">
                          {order.orderId || order.id?.substring(0, 8)?.toUpperCase()}
                        </p>
                        <p className="text-[10px] text-slate-500 mt-0.5 truncate max-w-[120px]">
                          {order.userName || order.userEmail}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <p className="text-xs font-bold text-white">{FormatPrice(order.total || 0)}</p>
                      <StatusBadge status={order.status} />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </GlassCard>
      </div>
      
      <style>{`
        .animate-spin-slow {
          animation: spin 3s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </motion.div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    Delivered: "bg-emerald-500/15 text-emerald-400 ring-emerald-500/30",
    Shipped: "bg-violet-500/15 text-violet-400 ring-violet-500/30",
    Placed: "bg-amber-500/15 text-amber-400 ring-amber-500/30",
    Approved: "bg-sky-500/15 text-sky-400 ring-sky-500/30",
    Declined: "bg-rose-500/15 text-rose-400 ring-rose-500/30",
    Cancelled: "bg-slate-500/15 text-slate-400 ring-slate-500/30",
  };
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ring-1 ${map[status] || map.Cancelled}`}>
      {status}
    </span>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f172a]/95 backdrop-blur border border-slate-700/60 rounded-xl px-4 py-3 shadow-2xl">
      <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">{label}</p>
      {payload.map((e, i) => (
        <p key={i} className="text-sm font-semibold" style={{ color: e.color }}>
          {e.name}: {typeof e.value === "number" && e.value > 100 ? FormatPrice(e.value) : e.value}
        </p>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN HOME LAYOUT (Sidebar + Topbar)
// ═══════════════════════════════════════════════════════════════════════════
const AdminHome = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const prevConnectedRef = useRef(true);

  // ✅ Déconnexion réelle et complète :
  // - Capacitor natif (si applicable)
  // - Firebase Auth JS SDK
  // - Nettoyage de TOUT état local résiduel (sessionStorage Pi + localStorage
  //   'adminUser' laissé par AuthContext), pour qu'un nouvel onglet ne
  //   retrouve jamais une "fausse" session encore valide.
  // ✅ Déconnexion complète et fiable
  const handleLogout = async () => {
    try {
      // 1. Déconnexion native (Capacitor)
      if (Capacitor.isNativePlatform()) {
        await FirebaseAuthentication.signOut();
      }

      // 2. Déconnexion Firebase Auth SDK
      await signOut(auth);

      // 3. Attendre que l'état Firestore soit mis à jour (petit délai)
      await new Promise(resolve => setTimeout(resolve, 300));

      // 4. Nettoyage complet des stockages locaux
      sessionStorage.removeItem('pi_user');
      localStorage.removeItem('adminUser');
      
      // 5. Si vous utilisez redux-persist, purgez le store
      // (décommentez si nécessaire)
      // import { persistor } from '../redux/store';
      // await persistor.purge();

      // 6. Rediriger vers la page de connexion
      toast.success("Déconnexion réussie !");
      window.location.href = "/login";
    } catch (error) {
      console.error('❌ Erreur lors de la déconnexion:', error);
      toast.error("Erreur lors de la déconnexion, réessayez.");
    }
  };

  const handleConnectivityChange = (connected) => {
    if (connected === prevConnectedRef.current) return;
    prevConnectedRef.current = connected;

    setIsOnline(connected);
    if (connected) {
      console.log('📶 Réseau restauré ! Synchronisation...');
      window.dispatchEvent(new CustomEvent('network-restored'));
      toast.success('📶 Connexion rétablie !');
    } else {
      toast.warning('📶 Connexion perdue. Certaines fonctionnalités sont désactivées.');
    }
  };

  const menuItems = [
    { path: "/", icon: Home, label: "Dashboard", accent: "text-amber-400" },
    { path: "/products", icon: Package, label: "Products", accent: "text-violet-400" },
    { path: "/orders", icon: ShoppingBag, label: "Orders", accent: "text-teal-400" },
    { path: "/eneo-transactions", icon: CreditCard, label: "Eneo Transactions", accent: "text-rose-400" },
    { path: "/pi-sell-requests", icon: PiSquare, label: "Pi Sell Requests", accent: "text-orange-400" },
    { path: "/pi-stores", icon: Store, label: "Pi Stores", accent: "text-cyan-400" },
    { path: "/shipping-settings", icon: Truck, label: "Shipping", accent: "text-purple-400" },
    { path: "/rate-settings", icon: SettingsIcon, label: "Rate Settings", accent: "text-amber-400" },
    { path: "/operator-accounts", icon: DollarSign, label: "Operator Accounts", accent: "text-emerald-400" },
    { path: "/whatsapp", icon: MessageCircle, label: "WhatsApp", accent: "text-rose-400" },
    { path: "/coupons", icon: Tag, label: "Coupons", accent: "text-emerald-400" },
    { path: "/delivery-tracking", icon: Truck, label: "Delivery Tracking", accent: "text-amber-400" },
    { path: "/users", icon: Users, label: "Users", accent: "text-pink-400" },
    { path: "/banners", icon: ImageIcon, label: "Banners", accent: "text-red-400" },
    { path: "/announcements", icon: Bell, label: "Announcements", accent: "text-blue-400" },
  ];

  const isActive = (item) => {
    if (item.path === "/") return location.pathname === "/";
    return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
  };

  const pageTitle = menuItems.find(i => isActive(i))?.label || "Dashboard";

  const SidebarContent = ({ onLinkClick }) => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 border-b border-white/[0.06]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30 overflow-hidden">
            <img 
              src={adminLogo} 
              alt="Sapi Admin" 
              className="w-7 h-7 object-contain" 
            />
          </div>
          <div>
            <p className="text-sm font-bold text-white tracking-tight">Sapi Admin</p>
            <p className="text-[10px] text-slate-500 tracking-widest uppercase">Management</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        <p className="text-[9px] uppercase tracking-[0.18em] text-slate-600 px-3 pb-2 pt-1">Menu</p>
        {menuItems.map((item, idx) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <motion.div key={item.path} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * idx }}>
              <Link
                to={item.path}
                onClick={onLinkClick}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 group ${
                  active ? "bg-white/[0.08] ring-1 ring-white/[0.1] text-white" : "text-slate-400 hover:text-white hover:bg-white/[0.05]"
                }`}
              >
                <Icon size={16} strokeWidth={active ? 2.2 : 1.8} className={active ? item.accent : "text-slate-500 group-hover:text-slate-300"} />
                <span className="font-medium">{item.label}</span>
                {active && <motion.div layoutId="activeDot" className={`ml-auto w-1.5 h-1.5 rounded-full ${item.accent.replace("text-", "bg-")}`} />}
              </Link>
            </motion.div>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-white/[0.06]">
        <button onClick={handleLogout} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all duration-200 group">
          <LogOut size={16} strokeWidth={1.8} className="group-hover:text-rose-400" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#0a0f1e] overflow-hidden" style={{ fontFamily: "'DM Sans', 'Outfit', system-ui, sans-serif" }}>
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            key="desktop-sidebar"
            initial={{ x: -280, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -280, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:flex w-60 flex-shrink-0 flex-col bg-[#0d1526]/90 backdrop-blur-2xl border-r border-white/[0.06] shadow-2xl"
          >
            <SidebarContent onLinkClick={undefined} />
          </motion.aside>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={() => setMobileMenuOpen(false)} />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-64 bg-[#0d1526] border-r border-white/[0.06] z-50 shadow-2xl flex flex-col"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                    <PiIcon size={18} />
                  </div>
                  <p className="text-sm font-bold text-white">Sapi Admin</p>
                </div>
                <button onClick={() => setMobileMenuOpen(false)} className="p-1.5 rounded-lg hover:bg-white/[0.06] text-slate-400 hover:text-white transition-colors">
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <SidebarContent onLinkClick={() => setMobileMenuOpen(false)} />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="flex-shrink-0 flex items-center justify-between px-4 md:px-6 py-3.5 border-b border-white/[0.06] bg-[#0a0f1e]/80 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-white/[0.06] text-slate-400 hover:text-white transition-colors">
              <Menu size={18} />
            </button>
            <button onClick={() => setSidebarOpen(v => !v)} className="hidden lg:flex p-2 rounded-lg hover:bg-white/[0.06] text-slate-400 hover:text-white transition-colors">
              <Menu size={18} />
            </button>
            <div>
              <h1 className="text-base font-bold text-white leading-none">{pageTitle}</h1>
              <p className="text-[10px] text-slate-500 mt-0.5 hidden sm:block">Pi Network Admin Panel</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <NetworkStatus onConnectivityChange={handleConnectivityChange} />
            <AdminNotificationBell isOnline={isOnline} />
            <AdminAvatarMenu />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 pt-6">
            {location.pathname === "/" ? <AdminDashboard /> : <Outlet />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminHome;